type StateChangeCallback<T = any> = (newValue: T, oldValue: T) => void;

export class StateManager<T extends Record<string, any>> {
  private state: T;
  private observers: Record<string, Array<StateChangeCallback>> = {};

  constructor(initialState: T) {
    this.state = { ...initialState };
  }

  // Initialize the state manager
  get<K extends keyof T>(key: K): T[K] {
    return this.state[key];
  }

  // Set state and notify observers
  set<K extends keyof T>(key: K, value: T[K]): boolean {
    const oldValue = this.state[key];
    this.state[key] = value;

    // Notify observers
    if (this.observers[key as string]) {
      this.observers[key as string].forEach((callback) =>
        callback(value, oldValue),
      );
    }

    return true;
  }

  // Subscribe to state changes
  onChange<K extends keyof T>(
    key: K,
    callback: StateChangeCallback<T[K]>,
  ): () => void {
    const keyStr = key as string;
    if (!this.observers[keyStr]) {
      this.observers[keyStr] = [];
    }
    this.observers[keyStr].push(callback as StateChangeCallback);

    // Return unsubscribe function
    return () => {
      this.observers[keyStr] = this.observers[keyStr].filter(
        (cb) => cb !== callback,
      );
    };
  }

  // Get entire state
  getAll(): T {
    return { ...this.state };
  }
}
